/**
 * Edit Role - Club admin interface for editing existing roles
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/api';
import {
  AdminPageLayout,
  FormInput,
  FormTextArea,
  FormCheckbox,
  Button,
  ConfirmDialog,
  LoadingState,
  Section,
  Card,
  Grid,
  Col
} from '../../components';
import { FormActionGroup } from '../../components/admin/patterns';
import { ErrorBanner } from '../../components/common';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useToast } from '../../contexts/ToastContext';

const EditRole = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const { showSuccess, showError } = useToast();
  const [originalData, setOriginalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const availablePermissions = [
    { id: 'user_management', name: 'User Management', description: 'Create, edit, and manage club users' },
    { id: 'program_management', name: 'Program Management', description: 'Manage training programs and schedules' },
    { id: 'player_evaluation', name: 'Player Evaluation', description: 'Conduct and view player assessments' },
    { id: 'skills_assessment', name: 'Skills Assessment', description: 'Evaluate player skills and progress' },
    { id: 'document_management', name: 'Document Management', description: 'Manage documents and requirements' },
    { id: 'reports', name: 'Reports', description: 'View club analytics and performance reports' },
    { id: 'settings', name: 'Club Settings', description: 'Configure club preferences and settings' },
    { id: 'registration', name: 'Registration', description: 'Manage player registrations' },
    { id: 'view_child_progress', name: 'View Child Progress', description: 'View progress of registered children' },
    { id: 'program_participation', name: 'Program Participation', description: 'Participate in assigned programs' }
  ];

  const {
    formData,
    errors,
    touched,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFormData,
    setFieldValue
  } = useFormValidation({
    initialValues: {
      name: '',
      description: '',
      permissions: [],
      is_active: true,
      is_custom: true,
      is_staff: false
    },
    validationSchema: (values) => {
      const newErrors = {};
      
      if (!values.name.trim()) {
        newErrors.name = 'Role name is required';
      }
      
      if (!values.description.trim()) {
        newErrors.description = 'Description is required';
      }
      
      if (values.permissions.length === 0) {
        newErrors.permissions = 'At least one permission is required';
      }
      
      return newErrors;
    },
    onSubmit: async (values) => {
      // Only send changed fields
      const changedData = {};
      Object.keys(values).forEach(key => {
        if (JSON.stringify(values[key]) !== JSON.stringify(originalData[key])) {
          changedData[key] = values[key];
        }
      });

      if (Object.keys(changedData).length === 0) {
        navigate('/admin/roles');
        return;
      }

      try {
        await apiService.updateClubRole(roleId, changedData);
        showSuccess(`Role "${values.name}" updated successfully`);
        navigate('/admin/roles');
      } catch (err) {
        const errorMsg = err.message || 'Failed to update role';
        setError(errorMsg);
        showError(errorMsg);
        throw err;
      }
    }
  });

  useEffect(() => {
    loadRole();
  }, [roleId]);

  const loadRole = async () => {
    try {
      setLoading(true);
      const response = await apiService.getClubRoles();
      const role = response.roles.find(r => r.id === roleId);
      
      if (!role) {
        setError('Role not found');
        setLoading(false);
        return;
      }

      const roleData = {
        name: role.name || '',
        description: role.description || '',
        permissions: role.permissions || [],
        is_active: role.is_active !== undefined ? role.is_active : true,
        is_custom: role.is_custom !== undefined ? role.is_custom : true,
        is_staff: role.is_staff !== undefined ? role.is_staff : false
      };

      setOriginalData(roleData);
      setFormData(roleData);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load role');
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionId) => {
    const currentPermissions = formData.permissions || [];
    const newPermissions = currentPermissions.includes(permissionId)
      ? currentPermissions.filter(p => p !== permissionId)
      : [...currentPermissions, permissionId];
    
    setFieldValue('permissions', newPermissions);
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteClubRole(roleId);
      showSuccess(`Role "${originalData.name}" deleted successfully`);
      navigate('/admin/roles');
    } catch (err) {
      const errorMsg = err.message || 'Failed to delete role';
      setError(errorMsg);
      showError(errorMsg);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout
        title="EDIT ROLE"
        subtitle="Loading role information..."
        backPath="/admin/roles"
        maxWidth="1400px"
      >
        <LoadingState message="Loading role..." />
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="EDIT ROLE"
      subtitle={`Editing: ${originalData.name}`}
      backPath="/admin/roles"
      maxWidth="1400px"
    >
      {error && (
        <ErrorBanner
          type="error"
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <form onSubmit={handleFormSubmit}>
        <Section title="ROLE INFORMATION">
          <Card level={1}>
            <Grid columns={2} gap="2rem">
              <Col>
                <FormInput
                  label="Role Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.name ? errors.name : ''}
                  placeholder="e.g., Assistant Coach, Team Manager"
                  disabled={!formData.is_custom}
                  required
                />
                {!formData.is_custom && (
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    marginTop: '0.5rem'
                  }}>
                    Default role names cannot be changed
                  </p>
                )}
              </Col>
              <Col span={2}>
                <FormTextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.description ? errors.description : ''}
                  placeholder="Describe the role and its responsibilities"
                  rows={3}
                  required
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="PERMISSIONS">
          <Card level={1}>
            {touched.permissions && errors.permissions && (
              <ErrorBanner type="error" message={errors.permissions} />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
              {availablePermissions.map(permission => (
                <div
                  key={permission.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '1rem',
                    backgroundColor: 'var(--surface-lvl2)',
                    borderRadius: '4px'
                  }}
                >
                  <FormCheckbox
                    label=""
                    name={`permission-${permission.id}`}
                    checked={formData.permissions.includes(permission.id)}
                    onChange={() => handlePermissionToggle(permission.id)}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: 'var(--color-text)', marginBottom: '0.25rem' }}>
                      {permission.name}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                      {permission.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Section>

        <Section title="STATUS">
          <Card level={1}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <FormCheckbox
                  label="Active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFieldValue('is_active', e.target.checked)}
                />
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--color-text-secondary)',
                  marginTop: '0.25rem',
                  marginLeft: '1.75rem'
                }}>
                  Inactive roles cannot be assigned to users
                </p>
              </div>

              <div>
                <FormCheckbox
                  label="Staff Role"
                  name="is_staff"
                  checked={formData.is_staff}
                  onChange={(e) => setFieldValue('is_staff', e.target.checked)}
                  disabled={!formData.is_custom}
                />
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--color-text-secondary)',
                  marginTop: '0.25rem',
                  marginLeft: '1.75rem'
                }}>
                  Staff roles are for club employees (admins, coaches, directors, managers). Used for event visibility filtering.
                  {!formData.is_custom && ' (Read-only for default roles)'}
                </p>
              </div>
            </div>
          </Card>
        </Section>

        <FormActionGroup
          onCancel={() => navigate('/admin/roles')}
          onSubmit={handleFormSubmit}
          submitLabel={isSubmitting ? 'SAVING...' : 'SAVE CHANGES'}
          isSubmitting={isSubmitting}
          submitDisabled={!isDirty}
          leftActions={
            formData.is_custom ? (
              <Button
                type="button"
                variant="danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSubmitting}
              >
                DELETE ROLE
              </Button>
            ) : null
          }
        />
      </form>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${formData.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </AdminPageLayout>
  );
};

export default EditRole;

